// FieldTable — table of all configurable fields for a selected module.
// Optimistic UI: switch state updates immediately, reverts on error.
// Nsofar Mandated = field is locked (platform-locked required field).

import { useState } from "react";
import type { JSX } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { FIELD_REGISTRY } from "@hcm/core";
import type { ModuleKey, FieldState } from "../lib/adminApiClient.js";
import { toggleField } from "../lib/adminApiClient.js";

interface FieldRow { key: string; active: boolean; required: boolean; locked: boolean; }

interface Props {
  tenantId: string;
  moduleKey: ModuleKey;
  fields: Record<string, FieldState>;
}

function Switch({ checked, disabled, onChange, label }: {
  checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string;
}): JSX.Element {
  return (
    <button
      role="switch" aria-checked={checked} aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 ${
        checked ? "bg-indigo-600" : "bg-slate-300"
      }`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-4" : "translate-x-0"
      }`} />
    </button>
  );
}

const TYPE_CLS: Record<string, string> = {
  number:  "bg-blue-50 text-blue-700",
  enum:    "bg-purple-50 text-purple-700",
  boolean: "bg-teal-50 text-teal-700",
  string:  "bg-slate-100 text-slate-600",
  array:   "bg-orange-50 text-orange-700",
  object:  "bg-amber-50 text-amber-700",
};

export function FieldTable({ tenantId, moduleKey, fields }: Props): JSX.Element {
  const registry = FIELD_REGISTRY[moduleKey] ?? {};
  const [rows, setRows] = useState<Record<string, FieldRow>>(() => {
    const r: Record<string, FieldRow> = {};
    for (const key of Object.keys(registry)) {
      const f = fields[key];
      r[key] = { key, active: f?.active ?? false, required: f?.required ?? false, locked: f?.locked ?? false };
    }
    return r;
  });

  async function commit(key: string, active: boolean, required: boolean): Promise<void> {
    const prev = rows[key];
    if (!prev) return; // guard: key must already exist in rows (set in useState init)
    setRows((r) => ({ ...r, [key]: { ...(r[key] ?? prev), active, required } }));
    try {
      await toggleField(tenantId, moduleKey, key, active, required);
      toast.success(`${key} updated.`);
    } catch {
      setRows((r) => ({ ...r, [key]: prev }));
      toast.error(`Failed to update ${key}.`);
    }
  }

  const allKeys = Object.keys(registry);
  if (allKeys.length === 0) {
    return <p className="text-sm text-slate-400 py-4">No configurable fields for this module.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide bg-slate-50">
            <th className="py-3 px-4 font-medium">Field</th>
            <th className="py-3 pr-4 font-medium">Label</th>
            <th className="py-3 pr-4 font-medium">Type</th>
            <th className="py-3 pr-4 font-medium">Active</th>
            <th className="py-3 pr-4 font-medium">Required</th>
            <th className="py-3 pr-4 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {/* eslint-disable-next-line complexity -- per-row render branches over locked/active/required + meta type */}
          {allKeys.map((key) => {
            const row = rows[key];
            if (!row) return null; // unreachable: rows is initialized from the same allKeys, but TS can't see that.
            const meta = registry[key];
            return (
              <tr key={key} className="hover:bg-slate-50 transition-colors">
                <td className="py-2.5 px-4 font-mono text-xs text-slate-700">{key}</td>
                <td className="py-2.5 pr-4 text-slate-600">{meta?.label ?? key}</td>
                <td className="py-2.5 pr-4">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${TYPE_CLS[meta?.type ?? "string"] ?? TYPE_CLS.string}`}>
                    {meta?.type ?? "string"}
                  </span>
                </td>
                <td className="py-2.5 pr-4">
                  <Switch
                    checked={row.active} disabled={row.locked} label={`active ${key}`}
                    onChange={(v) => void commit(key, v, v ? row.required : false)}
                  />
                </td>
                <td className="py-2.5 pr-4">
                  <Switch
                    checked={row.required} disabled={row.locked || !row.active} label={`required ${key}`}
                    onChange={(v) => void commit(key, row.active, v)}
                  />
                </td>
                <td className="py-2.5 pr-4">
                  {row.locked
                    ? <span className="flex items-center gap-1 text-xs text-amber-700 font-medium"><Lock className="h-3 w-3" />Nsofar Mandated</span>
                    : <span className="text-xs text-slate-400">—</span>
                  }
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
